import { Unzip, UnzipInflate } from 'fflate';

export type ImageSource =
  | { type: 'file'; file: File }
  | { type: 'zip'; zipFile: File };

export interface ImageFileSummary {
  name: string;
  sizeBytes: number;
  thumbnail: string;
  source: ImageSource;
}

const IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png']);
const THUMBNAIL_RENDER_SIZE = 200;

export const ACCEPTED_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'application/zip': ['.zip'],
  'application/x-zip-compressed': ['.zip'],
};

export const isZip = (file: File) =>
  file.type === 'application/zip' ||
  file.type === 'application/x-zip-compressed' ||
  file.name.endsWith('.zip');

export const isImageFilename = (name: string) => {
  const dot = name.lastIndexOf('.');
  if (dot === -1) return false;
  return IMAGE_EXTENSIONS.has(name.slice(dot).toLowerCase());
};

export const streamZipImages = (
  file: File,
  onImage: (name: string, sizeBytes: number, blob: Blob) => void
): Promise<void> =>
  _processZipEntries(file, (filename) => {
    if (filename.startsWith('.') || !isImageFilename(filename)) return null;
    return (data, size) => {
      onImage(filename, size, new Blob([data]));
      return false;
    };
  });

export const createImageSummary = async (
  name: string,
  sizeBytes: number,
  blobForThumbnail: Blob,
  source: ImageSource
): Promise<ImageFileSummary> => {
  const thumbnail = await createThumbnail(blobForThumbnail);
  return { name, sizeBytes, thumbnail, source };
};

export const createThumbnail = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(THUMBNAIL_RENDER_SIZE / img.width, THUMBNAIL_RENDER_SIZE / img.height);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((thumbnailBlob) => {
        if (!thumbnailBlob) { reject(new Error('Failed to create thumbnail')); return; }
        resolve(URL.createObjectURL(thumbnailBlob));
      }, 'image/jpeg', 0.7);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });

export const extractImageFromZip = async (zipFile: File, targetName: string): Promise<File> => {
  let file: File | undefined;

  await _processZipEntries(zipFile, (filename) => {
    if (filename !== targetName) return null;
    return (data) => {
      file = new File([data], targetName);
      return true; // stop streaming
    };
  });

  if (!file) throw new Error(`"${targetName}" not found in ZIP`);
  return file;
};

// Streams the ZIP file and processes matching entries. `getHandler` is called
// with each entry's filename; return null to skip it, or a function to decompress
// it and receive the assembled bytes — returning true from that function stops
// the stream early.
// UnzipInflate is the synchronous decompressor: entries are decompressed one at
// a time as data is pushed, so only one entry's bytes live in memory at once.
const _processZipEntries = (
  file: File,
  getHandler: (filename: string) => ((data: Uint8Array<ArrayBuffer>, size: number) => boolean) | null
): Promise<void> =>
  new Promise((resolve, reject) => {
    let done = false;

    const parser = new Unzip((zipEntry) => {
      if (zipEntry.name.endsWith('/')) return;
      const filename = zipEntry.name.split('/').pop() ?? zipEntry.name;
      if (!filename) return;

      const handle = getHandler(filename);
      if (!handle) return;

      const chunks: Uint8Array[] = [];
      let totalSize = 0;

      zipEntry.ondata = (err, chunk, final) => {
        if (err) return;
        chunks.push(chunk);
        totalSize += chunk.length;
        if (final) {
          const combined = new Uint8Array(totalSize) as Uint8Array<ArrayBuffer>;
          let offset = 0;
          for (const c of chunks) { combined.set(c, offset); offset += c.length; }
          if (handle(combined, totalSize)) done = true;
        }
      };

      zipEntry.start();
    });

    parser.register(UnzipInflate);

    (async () => {
      try {
        const reader = file.stream().getReader();
        try {
          while (true) {
            if (done) break;
            const { done: streamDone, value } = await reader.read();
            if (streamDone) break;
            parser.push(value);
          }
          if (!done) parser.push(new Uint8Array(0), true);
        } finally {
          reader.cancel();
        }
        resolve();
      } catch (err) {
        reject(err);
      }
    })();
  });
